import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ActionType } from '@new-signal/shared';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

interface AddStepMenuProps {
  onAdd: (type: ActionType) => void;
  disabledTypes?: ActionType[];
}

const ACTION_OPTIONS: { type: ActionType; labelKey: string; comingSoon?: boolean }[] = [
  { type: 'keyword_filter', labelKey: 'addStep.keywordFilter' },
  { type: 'ai_filter', labelKey: 'addStep.aiFilter' },
  { type: 'summarize', labelKey: 'addStep.summarize' },
  { type: 'translate', labelKey: 'addStep.translate' },
];

export function AddStepMenu({ onAdd, disabledTypes = [] }: AddStepMenuProps) {
  const { t } = useTranslation('feed-builder');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const aiFilterEnabled = useFeatureFlag('ai-filter-enabled');

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (type: ActionType) => {
    onAdd(type);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="w-full cursor-pointer rounded-xl border border-dashed border-brand-border-medium bg-transparent px-4 py-3 text-sm font-medium text-brand-muted hover:border-accent-blue hover:text-brand-text"
      >
        {t('addStep.button')}
      </button>

      {open && (
        <div className="absolute left-1/2 top-full z-10 mt-1 min-w-[220px] -translate-x-1/2 rounded-xl border border-brand-border bg-white shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
          {ACTION_OPTIONS.filter((option) => {
            if (option.type === 'ai_filter') return aiFilterEnabled;
            return true;
          }).map((option) => {
            const disabled = option.comingSoon || disabledTypes.includes(option.type);
            return (
              <button
                key={option.type}
                onClick={() => !disabled && handleSelect(option.type)}
                disabled={disabled}
                className={`block w-full border-none bg-transparent px-4 py-2.5 text-left text-sm first:rounded-t-xl last:rounded-b-xl ${
                  disabled
                    ? 'cursor-default text-brand-muted'
                    : 'cursor-pointer text-brand-text hover:bg-brand-surface'
                }`}
              >
                {t(option.labelKey)}
                {option.comingSoon && (
                  <span className="ml-1.5 text-[11px] text-brand-muted">
                    {t('addStep.comingSoon')}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
